import React, { useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { isSafeToCall, formatTimezone, getTimezoneForPhone } from '../utils/timezoneGuardrail';
import { formatCurrency } from '../utils/formatters';

const CALL_STATES = {
  idle: { label: 'Ready to Call', color: 'text-gray-500', icon: 'CALL' },
  checking: { label: 'Checking...', color: 'text-gray-500', icon: '...' },
  blocked: { label: 'Blocked by TCPA', color: 'text-amber-600', icon: 'STOP' },
  calling: { label: 'Initiating Call', color: 'text-tenafi-green', icon: 'CALL' },
  ringing: { label: 'Ringing...', color: 'text-tenafi-green', icon: 'RING' },
  connected: { label: 'Call Connected', color: 'text-green-600', icon: 'OK' },
  completed: { label: 'Call Completed', color: 'text-green-700', icon: 'OK' },
  voicemail: { label: 'Left Voicemail', color: 'text-blue-600', icon: 'VM' },
  no_answer: { label: 'No Answer', color: 'text-gray-500', icon: 'NA' },
  failed: { label: 'Call Failed', color: 'text-red-600', icon: 'ERR' },
};

export default function CallModal({ tenant, propertyId, propertyName, onClose, onCallLogged }) {
  const [callState, setCallState] = useState('idle');
  const [tzCheck, setTzCheck] = useState(null);
  const [callResult, setCallResult] = useState(null);
  const [error, setError] = useState('');
  const [lang, setLang] = useState(tenant?.preferredLanguage || 'en');

  useEffect(() => {
    if (!tenant?.phone) {
      return;
    }
    setTzCheck(isSafeToCall(tenant.phone));
  }, [tenant?.phone]);

  const handleCall = async () => {
    if (!tenant?.phone) {
      setError('No phone number on file for this tenant.');
      return;
    }

    setError('');
    setCallState('checking');

    const check = isSafeToCall(tenant.phone);
    setTzCheck(check);
    if (!check.safe) {
      setCallState('blocked');
      return;
    }

    setCallState('calling');

    try {
      const functions = getFunctions();
      const initiateCall = httpsCallable(functions, 'initiateVoiceCall');
      const result = await initiateCall({
        phone: tenant.phone,
        tenantId: tenant.id,
        propertyId,
        propertyName: propertyName || 'your property',
        tenantName: tenant.name,
        balance: tenant.currentBalance,
        lang,
      });

      setCallState('ringing');
      setCallResult(result.data);

      setTimeout(() => {
        setCallState(result.data?.status || 'completed');
        if (onCallLogged) {
          onCallLogged();
        }
      }, 4000);
    } catch (callError) {
      console.error('Call failed:', callError);
      setError(callError.message || 'Failed to initiate call.');
      setCallState('failed');
    }
  };

  const state = CALL_STATES[callState] || CALL_STATES.idle;
  const isActive = ['calling', 'ringing', 'connected'].includes(callState);
  const isDone = ['completed', 'voicemail', 'no_answer', 'failed'].includes(callState);

  if (!tenant) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">AI Voice Call</h2>
            <p className="text-sm text-gray-500 mt-0.5">{tenant.name} - Unit {tenant.unit}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">x</button>
        </div>

        <div className="p-6 space-y-5">
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Phone</span>
              <span className="font-medium text-gray-800">{tenant.phone || 'Not on file'}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-500">Balance</span>
              <span className="font-semibold text-red-600">{formatCurrency(tenant.currentBalance)}</span>
            </div>
            {tzCheck && (
              <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-100">
                <span className="text-gray-500">Tenant local time</span>
                <span className={`font-medium ${tzCheck.safe ? 'text-green-600' : 'text-amber-600'}`}>
                  {tzCheck.localTime} ({formatTimezone(getTimezoneForPhone(tenant.phone))})
                  {tzCheck.safe ? ' - Safe to call' : ' - Outside window'}
                </span>
              </div>
            )}
          </div>

          {callState === 'blocked' && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <p className="font-semibold mb-1">TCPA Compliance Block</p>
              <p>{tzCheck?.reason}</p>
              <p className="mt-2 text-xs text-amber-600">Calls are restricted to 8:00 AM to 9:00 PM in the tenant local time zone.</p>
            </div>
          )}

          <div className="bg-tenafi-light border border-tenafi-green/20 rounded-xl p-4 text-xs text-tenafi-green-dark space-y-1">
            <p className="font-semibold text-sm mb-2">Auto-Included Disclosures</p>
            <p>AI or automated call disclosure</p>
            <p>Debt collection disclosure when appropriate</p>
            <p>Opt-out instructions for the tenant</p>
            <p>Call recording notice</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Call Language</label>
            <div className="flex gap-2">
              <button
                onClick={() => setLang('en')}
                className={
                  'px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ' +
                  (lang === 'en' ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                }
              >
                English
              </button>
              <button
                onClick={() => setLang('es')}
                className={
                  'px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ' +
                  (lang === 'es' ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                }
              >
                Español
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{state.icon}</span>
            <div>
              <p className={`font-semibold ${state.color}`}>{state.label}</p>
              {isActive && <p className="text-xs text-gray-400 mt-0.5">The ElevenLabs agent is handling the call.</p>}
              {isDone && callResult && <p className="text-xs text-gray-400 mt-0.5">Call ID: {callResult.callId || 'N/A'}</p>}
            </div>
            {isActive && (
              <div className="ml-auto w-5 h-5 border-2 border-tenafi-green border-t-transparent rounded-full animate-spin" />
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            {isDone ? 'Close' : 'Cancel'}
          </button>
          {!isDone && callState !== 'blocked' && (
            <button
              onClick={handleCall}
              disabled={isActive || !tenant.phone || (tzCheck && !tzCheck.safe)}
              className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isActive ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Calling...
                </>
              ) : (
                'Start AI Call'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
